import * as React from "react";
import { Combobox, Field, Input, InputGroup } from "@/components/ui";
import { cn } from "@/lib/utils";
import { stripCTAPrefix } from "@/services/bedrock";
import { CtaTypeDefinitions, formatCTAPhone } from "../ctaTypes";
import { useCTAWizard } from "../CTAWizardContext";
import { MissingValueWarning } from "./MissingValueWarning";
import { ReadonlyCtaUrl } from "./ReadonlyCtaUrl";

export const ConfigureStep: React.FC = () => {
  const {
    selectedGroup,
    label,
    setLabel,
    labelError,
    setLabelError,
    typeError,
    linkError,
    setLinkError,
    linkValue,
    setLinkValue,
    ctaType,
    groupTypes,
    applyTypeSelection,
    isPlayer,
    forms,
    smartLinks,
    formResolvedUrl,
    smartLinkResolvedUrl,
    resolvedPreview,
    missingValueWarning,
  } = useCTAWizard();

  if (!selectedGroup) return null;

  return (
    <div className="flex flex-col gap-4">
      <Field name="ctaWizardLabel" label="Button Label" error={labelError ?? undefined}>
        <Input
          value={label}
          onChange={e => {
            setLabel(e.target.value);
            if (labelError) setLabelError(null);
          }}
          maxLength={50}
          placeholder="e.g. Learn More"
          className="w-full"
        />
      </Field>

      <Field name="ctaWizardDestination" label="Destination" error={typeError ?? undefined}>
        <div className="flex flex-col gap-2">
          {groupTypes.map(type => {
            const typeDefinition = CtaTypeDefinitions[type];
            const selected = ctaType === type;
            return (
              <div
                key={type}
                className={cn(
                  "rounded-lg border transition-colors",
                  selected
                    ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                    : "border-border hover:border-primary/40"
                )}
              >
                <button
                  type="button"
                  title={typeDefinition.description(isPlayer)}
                  onClick={() => applyTypeSelection(type)}
                  className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left cursor-pointer"
                >
                  <span
                    className={cn(
                      "size-4 shrink-0 rounded-full border-2 flex items-center justify-center",
                      selected ? "border-primary" : "border-slate-300"
                    )}
                  >
                    {selected && <span className="size-2 rounded-full bg-primary" />}
                  </span>
                  <span className={cn("text-primary shrink-0", !selected && "opacity-60")}>{typeDefinition.icon}</span>
                  <span
                    className={cn(
                      "text-sm font-semibold leading-snug truncate",
                      selected ? "text-slate-900" : "text-slate-600"
                    )}
                  >
                    {typeDefinition.title(isPlayer)}
                  </span>
                </button>

                {selected && (
                  <div className="flex flex-col gap-2.5 border-t border-primary/10 px-3 pb-3 pt-2.5">
                    <p className="mb-0 text-xs text-muted-foreground">{typeDefinition.description(isPlayer)}</p>

                    {(typeDefinition.input === "link" ||
                      typeDefinition.input === "phone" ||
                      typeDefinition.input === "email") && (
                      <Field name="ctaWizardLink" error={linkError ?? undefined}>
                        <InputGroup
                          className="w-full flex-grow bg-white"
                          control={controlProps => (
                            <Input
                              {...controlProps}
                              value={linkValue}
                              onChange={e => {
                                setLinkValue(e.target.value);
                                if (linkError) setLinkError(null);
                              }}
                              onBlur={() => {
                                setLinkValue(value => {
                                  const stripped = stripCTAPrefix(value, typeDefinition.prefix);
                                  return typeDefinition.input === "phone" ? formatCTAPhone(stripped) : stripped;
                                });
                              }}
                              maxLength={255}
                              placeholder={typeDefinition.placeholder}
                              className="w-full flex-grow"
                            />
                          )}
                          prepend={
                            <p className="mb-0 font-normal border-r border-input pr-2 text-muted-foreground">
                              {typeDefinition.prefix}
                            </p>
                          }
                        />
                      </Field>
                    )}

                    {typeDefinition.input === "form" && (
                      <div className="flex flex-col gap-2.5">
                        <Field name="ctaWizardForm" error={linkError ?? undefined}>
                          <Combobox
                            {...forms}
                            onValueChange={(value: any, eventDetails: any) => {
                              forms.onValueChange(value, eventDetails);
                              if (linkError) setLinkError(null);
                            }}
                            placeholder="Search for a Form..."
                            className="w-full bg-white"
                          />
                        </Field>
                        {formResolvedUrl ? (
                          <ReadonlyCtaUrl url={formResolvedUrl} />
                        ) : (
                          forms.value && (
                            <p className="mb-0 text-sm text-muted-foreground">
                              The form link is created for each Player when the page is viewed.
                            </p>
                          )
                        )}
                      </div>
                    )}

                    {typeDefinition.input === "smartLink" && (
                      <div className="flex flex-col gap-2.5">
                        <Field name="ctaWizardSmartLink" error={linkError ?? undefined}>
                          <Combobox
                            {...smartLinks}
                            onValueChange={(value: any, eventDetails: any) => {
                              smartLinks.onValueChange(value, eventDetails);
                              if (linkError) setLinkError(null);
                            }}
                            placeholder="Search for a Smart Link..."
                            className="w-full bg-white"
                          />
                        </Field>
                        {smartLinkResolvedUrl && <ReadonlyCtaUrl url={smartLinkResolvedUrl} />}
                      </div>
                    )}

                    {typeDefinition.input === "resolved" &&
                      (resolvedPreview ? (
                        <ReadonlyCtaUrl url={resolvedPreview} prefix={typeDefinition.prefix} />
                      ) : missingValueWarning ? (
                        <MissingValueWarning message={missingValueWarning} className="px-3 py-2.5" />
                      ) : (
                        <p className="mb-0 text-sm text-muted-foreground">
                          Resolved automatically for each Player when the page is viewed.
                        </p>
                      ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Field>
    </div>
  );
};
